import { Controller } from "@hotwired/stimulus"
import { useStore } from "stimulus-store"
import { areaStore } from "../../../stores/locales/area_store"
import { allRoomsStore } from "../../../stores/locales/all_rooms_store"

/**
 * Supplier controller for rooms in the currently selected area.
 *
 * Reacts to areaStore changes and fetches all rooms for that area
 * from the /api/locales/rooms/list endpoint. Results are stored in
 * allRoomsStore as an array of { id, name } objects.
 *
 * Connects to data-controller="suppliers--locales--rooms-supplier"
 */
export default class extends Controller {

    static stores = [
        allRoomsStore,
        areaStore
    ]

    connect() {
        useStore(this)
    }

    // Auto-called by stimulus-store when areaStore changes
    onAreaStoreUpdate(area) {
        if (!area || !area.id) {
            // Area cleared — reset rooms
            this.setAllRoomsStoreValue([])
            return
        }

        this.loadRooms(area)
    }

    async loadRooms(area) {
        // Clear previous rooms while loading
        this.setAllRoomsStoreValue([])

        try {
            const rooms = await this.fetchAllRooms(area)
            this.setAllRoomsStoreValue(rooms)
        } catch (error) {
            console.error('Error loading rooms:', {
                error: error.message,
                areaId: area.id,
                timestamp: new Date().toISOString()
            })
            this.setAllRoomsStoreValue([])
        }
    }

    async fetchAllRooms(area) {
        const rooms = []
        let paginationToken = null

        do {
            const params = new URLSearchParams({
                region: area.region,
                building: area.building,
                area: area.id
            })
            if (paginationToken) params.append('pagination_token', paginationToken)

            const response = await fetch(`/api/locales/rooms/list?${params}`)
            if (!response.ok) throw new Error(`HTTP ${response.status}`)

            const data = await response.json()
            rooms.push(...data.rooms)
            paginationToken = data.pagination_token
        } while (paginationToken)

        return rooms.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    }
}
